import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Lottie from "lottie-react";
import axios from "axios";
import { getValidToken } from "../../utils/tokenManager";
import close from "../../assets/dashboard/close-icon.svg";
import note from "../../assets/agent/last-note.svg";
import loadingAnim from "../../assets/common/json-Animacin-carga-.json";
import { TourProvider, useTour } from "@reactour/tour";
import { getClientPopupTourSteps } from "../admin/adminTourSteps";

const ContactDetailsModal = ({ open, onClose, data = {} }) => {
  const { t } = useTranslation();
  const steps = React.useMemo(() => getClientPopupTourSteps(t), [t]);

  if (!open) return null;

  return (
    <TourProvider steps={steps}>
      <ModalContent onClose={onClose} data={data} />
    </TourProvider>
  );
};

const ModalContent = ({ onClose, data }) => {
  const { setIsOpen } = useTour();
  const { t } = useTranslation();
  const modalRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const notesPopupRef = useRef(null);
  const rowRefs = useRef({});
  const [openContactId, setOpenContactId] = useState(null);
  const [notesByContact, setNotesByContact] = useState({});
  const [contacts, setContacts] = useState([]);
  const [error, setError] = useState(null);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartYRef = useRef(0);
  const dragStartScrollTopRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const fetchContactDetails = async () => {
      if (!data.metricType || !data.dateRange) {
        setContacts([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const accessToken = await getValidToken();

        const config = {
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/dashboard/contacts/details`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            metric_type: data.metricType,
            start_date: data.dateRange?.start_date,
            end_date: data.dateRange?.end_date,
            page: 1,
            limit: 50,
          },
        };

        const response = await axios(config);
        setContacts(response.data.contacts || []);
      } catch (err) {
        console.error("Error fetching contact details:", err);
        setError(err.response?.data?.error || t("error.generic"));
        setContacts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchContactDetails();
  }, [data.metricType, data.dateRange]);

  const handleNoteToggle = (contactId) => {
    setOpenContactId((prev) => (prev === contactId ? null : contactId));
  };

  const fetchNotes = async (contactId) => {
    if (notesByContact[contactId]) return;

    setLoad(true);

    try {
      const token = await getValidToken();
      const res = await axios.get(
        `${process.env.REACT_APP_APIBASEURL}/api/calls/${contactId}/notes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNotesByContact((prev) => ({
        ...prev,
        [contactId]: res.data || [],
      }));
    } catch (e) {
      setNotesByContact((prev) => ({
        ...prev,
        [contactId]: [],
      }));
    } finally {
      setLoad(false);
    }
  };


  React.useEffect(() => {
    const role = sessionStorage.getItem("userRole");
    const hasSeen = localStorage.getItem("kikonnect_popup_tour_shown");
    if (role !== "Admin" || hasSeen) return;

    const timer = setTimeout(() => {
      setIsOpen(true);
      localStorage.setItem("kikonnect_popup_tour_shown", "1");
    }, 500);

    return () => clearTimeout(timer);
  }, [setIsOpen]);

  const recalcThumb = () => {
    const sc = scrollContainerRef.current;
    const tr = trackRef.current;
    if (!sc || !tr) return;

    const { scrollHeight, clientHeight, scrollTop } = sc;
    const trackHeight = tr.clientHeight;

    if (scrollHeight <= clientHeight) {
      setThumbHeight(trackHeight);
      setThumbTop(0);
      return;
    }

    const newThumbHeight = Math.max(
      (clientHeight / scrollHeight) * trackHeight,
      24
    );
    setThumbHeight(newThumbHeight);

    const maxThumbTop = trackHeight - newThumbHeight;
    const newTop = (scrollTop / (scrollHeight - clientHeight)) * maxThumbTop;
    setThumbTop(newTop);
  };

  const handleScroll = () => recalcThumb();

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    const sc = scrollContainerRef.current;
    if (!sc) return;
    setIsDragging(true);
    dragStartYRef.current = e.clientY;
    dragStartScrollTopRef.current = sc.scrollTop;
  };

  useEffect(() => {
    recalcThumb();
    const onResize = () => recalcThumb();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [contacts]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging) return;
      const sc = scrollContainerRef.current;
      const tr = trackRef.current;
      if (!sc || !tr) return;

      const trackHeight = tr.clientHeight;
      const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
      const { scrollHeight, clientHeight } = sc;

      const deltaY = e.clientY - dragStartYRef.current;
      const scrollRange = Math.max(scrollHeight - clientHeight, 1);
      const thumbRange = Math.max(maxThumbTop, 1);
      const scrollDelta = (deltaY / thumbRange) * scrollRange;

      sc.scrollTop = Math.min(
        Math.max(dragStartScrollTopRef.current + scrollDelta, 0),
        scrollRange
      );
      recalcThumb();
    };

    const onMouseUp = () => {
      if (isDragging) setIsDragging(false);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isDragging, thumbHeight]);

  const handleBackdropClick = (e) => {
    if (modalRef.current && e.target === modalRef.current) {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
    setContacts([]);
    setError(null);
  };

  const contactData = contacts.map((contact) => ({
    id: contact.id,
    company: contact.company || "",
    fullName:
      `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "-",
    role: contact.role || "-",
    email: contact.email || "-",
    phone: contact.phone || contact.mobile || "-",
  }));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openContactId &&
        notesPopupRef.current &&
        !notesPopupRef.current.contains(e.target)
      ) {
        setOpenContactId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openContactId]);

  const handleNotesBackdropClick = () => {
    setOpenContactId(null);
  };

  const getPopupPosition = (contactId) => {
    const rowEl = rowRefs.current[contactId];
    const container = scrollContainerRef.current;

    if (!rowEl || !container) return "bottom";

    const rowRect = rowEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const spaceBelow = containerRect.bottom - rowRect.bottom;
    const spaceAbove = rowRect.top - containerRect.top;

    return spaceBelow < 250 && spaceAbove > spaceBelow ? "top" : "bottom";
  };


  return (
    <div
      ref={modalRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4"
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[900px] h-[658px] relative">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 text-xl font-bold z-10"
        >
          <img src={close} alt="close" />
        </button>

        <div className="p-7 relative flex">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-y-auto h-[557px] flex-1 scrollbar-none"
          >
            <table className="w-full text-primary">
              <thead className="top-0 bg-white">
                <tr className="text-icon border-b border-stroke font-semibold text-xl text-center divide-x divide-stroke">
                  <th className="py-3 px-4 w-[127px]">
                    {t("dashboard.clientDashboard.interestedCompanies")}
                  </th>
                  <th className="py-3 px-4 w-[127px]">{t("admin.fullName")}</th>
                  <th className="py-3 px-4 w-[127px]">{t("admin.role")}</th>
                  <th className="py-3 px-4 w-[127px]">{t("email")}</th>
                  <th className="py-3 px-4 w-[127px]">{t("admin.phone")}</th>
                </tr>
              </thead>
              <tbody>
                <>
                  {error ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="flex items-center justify-center h-[557px]">
                          <p className="text-red-500">{error}</p>
                        </div>
                      </td>
                    </tr>
                  ) : loading ? (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
                      <div className="bg-white rounded-lg text-secondary text-xl shadow-xl p-6 relative flex flex-col items-center">
                        <Lottie
                          animationData={loadingAnim}
                          loop
                          autoplay
                          style={{ height: "5rem", width: "5rem" }}
                        />
                      </div>
                    </div>
                  ) : contactData.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="flex items-center justify-center h-[557px]">
                          <p className="text-primary">
                            {t("dashboard.clientDashboard.noDetails")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    contactData.map((contact, index) => (
                      <tr
                        ref={(el) => (rowRefs.current[contact.id] = el)}
                        key={contact.id || index}
                        className="border-b border-stroke text-center text-sm text-primary divide-x divide-stroke relative"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="relative flex items-center justify-center w-6 h-6 shrink-0 cursor-pointer group"
                              onClick={() => {
                                handleNoteToggle(contact.id);
                                fetchNotes(contact.id);
                              }}
                              data-tour-id="company-note"
                            >
                              <img
                                src={note}
                                alt="note"
                                className="w-4 h-4 object-contain block"
                              />

                              {/* Tooltip */}
                              <div className="absolute left-7 top-1/2 -translate-y-1/2 w-[150px] px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 whitespace-normal">
                                {t("dashboard.clientDashboard.noteTooltip")}
                              </div>
                            </button>

                            <span>{contact.company}</span>

                            {openContactId === contact.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={handleNotesBackdropClick}
                                />

                                <div
                                  className={`absolute z-50 bg-white shadow-lg rounded-md px-4 py-6 border border-secondary text-left w-[800px]
                                  ${getPopupPosition(contact.id) === "top"
                                      ? "bottom-full mb-2"
                                      : "top-full mt-2"}
                                  `}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {load && (
                                    <div className="relative flex flex-col items-center">
                                      <Lottie
                                        animationData={loadingAnim}
                                        loop
                                        autoplay
                                        style={{ height: "2rem", width: "2rem" }}
                                      />
                                    </div>
                                  )}

                                  <button
                                    onClick={() => setOpenContactId(null)}
                                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
                                    aria-label="Close notes"
                                  >
                                    ✕
                                  </button>

                                  {(notesByContact[contact.id] || []).map((note, i) => (
                                    <div key={i} className="mb-4">
                                      <div className="flex gap-2">
                                        <p className="text-secondary text-sm">
                                          {new Date(note.date).toLocaleDateString()}
                                        </p>
                                        <p className="text-gray-600">({note.type})</p>
                                      </div>
                                      <p className="text-primary mt-2">{note.content}</p>
                                    </div>
                                  ))}

                                  {notesByContact[contact.id]?.length === 0 && (
                                    <p className="text-gray-500">No notes available.</p>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4">{contact.fullName}</td>
                        <td className="py-3 px-4">{contact.role}</td>
                        <td className="py-3 px-4">{contact.email}</td>
                        <td className="py-3 px-4">{contact.phone}</td>
                      </tr>
                    ))
                  )}
                </>
              </tbody>
            </table>
          </div>
          {!error && contactData.length > 0 && (
            <div
              ref={trackRef}
              className="absolute right-4 top-20 bottom-10 w-[6px] bg-buttonFalse rounded-full select-none"
            >
              <div
                ref={thumbRef}
                onMouseDown={onThumbMouseDown}
                style={{ height: `${thumbHeight}px`, top: `${thumbTop}px` }}
                className="w-[6px] bg-primary rounded-full absolute left-0 cursor-grab active:cursor-grabbing"
              ></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsModal;
